<script setup lang="ts">
import BrowseJobsHeader from "@/components/marketing/browse-jobs/BrowseJobsHeader.vue";
import BrowseJobsFilters from "@/components/marketing/browse-jobs/BrowseJobsFilters.vue";
import BrowseJobsFeaturedCompanies from "@/components/marketing/browse-jobs/BrowseJobsFeaturedCompanies.vue";
import BrowseJobsResults from "@/components/marketing/browse-jobs/BrowseJobsResults.vue";
import CallToAction from "@/components/marketing/CallToAction.vue";
import FooterCTABar from "@/components/marketing/FooterCTABar.vue";
import type { JobWithMetaDescription, SelectItem } from "@/types";
import type { Pagination, BrowseJobsPageData } from "@/types/marketing";
import type { Option } from "@/types";
import type { Company } from "@/dto/company/company";

const data = (await useSeoAndGetContent(ROUTES.browseJobs)) as unknown as BrowseJobsPageData & {
  header: any;
  stats: any;
  callToAction: any;
  footerCTA: any;
};

definePageMeta({
  layout: "marketing",
});

const searchQuery = ref("");
const selectedWorkingModel = ref<SelectItem | undefined>(undefined);
const selectedExperience = ref<SelectItem | undefined>(undefined);
const selectedCapacity = ref<SelectItem | undefined>(undefined);
const selectedCompanyId = ref<number | undefined>(undefined);
const selectedLocation = ref<
  | {
      placeId: string;
      formattedAddress: string;
      addressComponents: any[];
      lat: number;
      lng: number;
    }
  | undefined
>(undefined);
const locationQuery = ref("");
const currentPage = ref(1);
const isRefreshingFilters = ref(false);
const parentSource = ref("");

// Set parentSource on client side only
onMounted(() => (parentSource.value = window.location.href));

// Function to fetch filter options with company ID
const fetchFilterOptions = async (companyId?: number) => {
  try {
    const options = await $fetch<{
      working_model: Option[];
      experience: Option[];
      capacity: Option[];
    }>(API_ROUTES.job.public.filterOptions, {
      query: { companyId },
    });
    filterOptions.value = options;
  } catch (error) {
    console.error("Error fetching filter options:", error);
  }
};
const jobsPerPage = 20;

// Timer for debouncing
const refreshTimer = ref<ReturnType<typeof setTimeout> | null>(null);

// Use single API endpoint for both regular and location-based searches
const apiUrl = computed(() => {
  return API_ROUTES.job.public.index;
});

const queryParams = computed(() => {
  const baseParams = {
    page: currentPage.value,
    limit: jobsPerPage,
    search: searchQuery.value,
    workingModel: selectedWorkingModel.value?.id,
    experience: selectedExperience.value?.id,
    capacity: selectedCapacity.value?.id,
    companyId: selectedCompanyId.value,
  };

  // Add location-specific params if location is selected
  if (selectedLocation.value) {
    const locationParams = {
      ...baseParams,
      placeId: selectedLocation.value.placeId,
      formattedAddress: selectedLocation.value.formattedAddress,
      addressComponents: JSON.stringify(selectedLocation.value.addressComponents),
      lat: selectedLocation.value.lat,
      lng: selectedLocation.value.lng,
    };

    return locationParams;
  }

  return baseParams;
});

const {
  data: jobsData,
  status,
  refresh,
  error,
} = await useLazyAsyncData(
  "browse-jobs",
  () =>
    $fetch<{
      jobs: Array<JobWithMetaDescription>;
      pagination: Pagination;
    }>(apiUrl.value, {
      query: queryParams.value,
    }),
  {
    server: false,
    default: () => ({ jobs: [], pagination: { total: 0, page: 1, limit: jobsPerPage, totalPages: 0 } }),
  }
);

// Clear any existing timer
const clearRefreshTimer = () => {
  if (refreshTimer.value) {
    clearTimeout(refreshTimer.value);
    refreshTimer.value = null;
  }
};

// Debounced refresh function
const debouncedRefresh = (delay: number = 300) => {
  clearRefreshTimer();
  refreshTimer.value = setTimeout(() => {
    refresh();
  }, delay);
};

// Handle location selection
const handleLocationSelected = (location: any) => {
  selectedLocation.value = location;
  currentPage.value = 1;
  debouncedRefresh();
};

// Clear location search
const clearLocationSearch = () => {
  selectedLocation.value = undefined;
  locationQuery.value = "";
  currentPage.value = 1;
  debouncedRefresh();
};

// Watch for filter changes and manually refresh
watch([selectedWorkingModel, selectedExperience, selectedCapacity, selectedCompanyId, selectedLocation], () => {
  currentPage.value = 1;
  debouncedRefresh();
});

// Watch for company selection and refresh filter options
watch(selectedCompanyId, async () => {
  isRefreshingFilters.value = true;

  // Clear selected filters when company changes
  selectedWorkingModel.value = undefined;
  selectedExperience.value = undefined;
  selectedCapacity.value = undefined;

  // Fetch filter options with the new company ID
  await fetchFilterOptions(selectedCompanyId.value);
  isRefreshingFilters.value = false;
});

// Watch search query separately with its own debouncing
watch(searchQuery, () => {
  currentPage.value = 1;
  debouncedRefresh(500); // Longer delay for search typing
});

// Watch page changes without debouncing
watch(currentPage, () => {
  refresh();
});

// Cleanup timer on unmount
onUnmounted(() => {
  clearRefreshTimer();
});

// Combined loading states
const jobsLoading = computed(() => status.value === "pending");
const filtersLoading = computed(() => filterOptionsStatus.value === "pending" || isRefreshingFilters.value);
const companiesLoading = computed(() => companiesStatus.value === "pending");

const pending = computed(() => status.value === "pending");
const jobs = computed(() => jobsData.value?.jobs || []);
const pagination = computed(() => jobsData.value?.pagination || { total: 0, page: 1, limit: jobsPerPage, totalPages: 0 });

const { data: filterOptions, status: filterOptionsStatus } = await useLazyAsyncData(
  "job-filter-options",
  () =>
    $fetch<{
      working_model: Option[];
      experience: Option[];
      capacity: Option[];
    }>(API_ROUTES.job.public.filterOptions, {
      query: { companyId: selectedCompanyId.value },
    }),
  {
    server: false,
    default: () => ({ working_model: [], experience: [], capacity: [] }),
  }
);

const filtersPending = computed(() => filtersLoading.value);

const { data: featuredCompanies, status: companiesStatus } = await useLazyAsyncData(
  "featured-companies",
  () => $fetch<Company[]>(API_ROUTES.company.featured),
  {
    server: false,
    default: () => [],
  }
);

const workingModels = computed(() => {
  return (filterOptions.value?.working_model || []).map((item: any) => ({
    id: item.name,
    name: item.name,
    count: item.count,
  })) as SelectItem[];
});

const experiences = computed(() => {
  return (filterOptions.value?.experience || []).map((item: any) => ({
    id: item.name,
    name: item.name,
    count: item.count,
  })) as SelectItem[];
});

const capacities = computed(() => {
  return (filterOptions.value?.capacity || []).map((item: any) => ({
    id: item.name,
    name: item.name,
    count: item.count,
  })) as SelectItem[];
});

const clearFilters = () => {
  searchQuery.value = "";
  selectedWorkingModel.value = undefined;
  selectedExperience.value = undefined;
  selectedCapacity.value = undefined;
  selectedCompanyId.value = undefined;
  selectedLocation.value = undefined;
  locationQuery.value = "";
  currentPage.value = 1;
};

const hasActiveFilters = computed(() => {
  return !!(
    searchQuery.value ||
    selectedWorkingModel.value ||
    selectedExperience.value ||
    selectedCapacity.value ||
    selectedCompanyId.value ||
    selectedLocation.value
  );
});

const goToPage = (page: number) => {
  currentPage.value = page;
  window.scrollTo({ top: 0, behavior: "smooth" });
};
</script>

<template>
  <div class="relative bg-warm-white overflow-x-hidden">
    <div class="flex flex-col">
      <!-- Hero Header -->
      <BrowseJobsHeader :content="data.header" />

      <!-- Filters Section -->
      <BrowseJobsFilters
        v-model:search-query="searchQuery"
        v-model:location-query="locationQuery"
        v-model:selected-working-model="selectedWorkingModel"
        v-model:selected-experience="selectedExperience"
        v-model:selected-capacity="selectedCapacity"
        :working-models="workingModels"
        :experiences="experiences"
        :capacities="capacities"
        :filters-pending="filtersPending"
        :has-active-filters="hasActiveFilters"
        @location-selected="handleLocationSelected"
        @location-cleared="clearLocationSearch"
        @clear-filters="clearFilters"
      />

      <!-- Featured Companies -->
      <BrowseJobsFeaturedCompanies
        v-if="featuredCompanies && featuredCompanies.length > 0"
        :companies="featuredCompanies"
        :selected-company-id="selectedCompanyId"
        :loading="companiesLoading"
        @update:selected-company-id="selectedCompanyId = $event"
      />

      <!-- Job Results -->
      <BrowseJobsResults
        :jobs="jobs"
        :pagination="pagination"
        :pending="pending"
        :parent-source="parentSource"
        :search-query="searchQuery"
        :selected-location="selectedLocation"
        :selected-capacity="selectedCapacity"
        :selected-experience="selectedExperience"
        :selected-working-model="selectedWorkingModel"
        :current-page="currentPage"
        @go-to-page="goToPage"
      />

      <!-- Call to Action -->
      <CallToAction :content="data.callToAction" />

      <!-- Footer CTA Bar -->
      <FooterCTABar :content="data.footerCTA" />
    </div>
  </div>
</template>
